import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { faChevronRight } from '@fortawesome/pro-light-svg-icons/faChevronRight';
import { faClock } from '@fortawesome/pro-light-svg-icons/faClock';
import { faCheck } from '@fortawesome/pro-light-svg-icons/faCheck';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TypedMemo } from '../../../common/types';
import { ErrorAlert } from '../../../common/views/ErrorAlert/ErrorAlert';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
import { config } from '../../../../config/config';
import { AuthCodeInput } from './AuthCodeInput';
import { AuthFieldError } from './AuthFieldError';
import { authErrorKind } from './authErrorKind';
import { AuthRateLimitText } from './AuthRateLimitText';
import { authV2Messages } from './authV2Messages';
import { renderBold } from './authRichText';
import { formatCountdown, useCountdown } from './useCountdown';
import { RESEND_WAIT_SECONDS } from '../authCodeStepStorage';

/**
 * The page offers a new code a minute after the last one (`RESEND_WAIT_SECONDS`). This is a page
 * rule only: the API limits code requests per hour and code checks per 15 minutes
 * (`loginCodeRateLimit.ts`). After "Too many tries" the page shows the wait that the API sent
 * (Ruling F5), and no countdown.
 */

/** The seconds of the resend wait that are left for a code sent at `sentAt` (ms). */
function resendWaitLeft(sentAt: number | null): number {
  if (sentAt === null) {
    return 0;
  }
  const passed = Math.floor((Date.now() - sentAt) / 1000);
  return Math.max(0, Math.min(RESEND_WAIT_SECONDS, RESEND_WAIT_SECONDS - passed));
}

interface AuthCodeStepProps {
  email: string;
  code: string;
  setCode: (v: string) => void;
  /** Checks the code in the field, or `value` (the sixth digit submits by itself, QA imp-4). */
  onVerify: (e?: React.SyntheticEvent | null, value?: string) => void;
  /** The code in the field was refused: Continue waits for another code (QA brk-15). */
  isRefusedCode?: boolean;
  /** When the code on screen went out (ms); the resend wait counts from it. Null: no wait. */
  codeSentAt?: number | null;
  onResend: () => void;
  onBack: () => void;
  isLoading: boolean;
  isResending: boolean;
  error: Error | null;
  /** The hidden Turnstile check (`TurnstileSlot`). The resend needs a fresh token. */
  securityCheck?: React.ReactNode;
  /** The check waits for a tick (`useTurnstile().needsInteraction`): the send stays off. */
  securityCheckPending?: boolean;
}

/**
 * The 6-digit code step. design: onboarding g6 (typing, wrong code, too many tries, code resent).
 * The page swaps to this step in place; the visual on the right stays.
 */
const AuthCodeStepComponent: React.FC<AuthCodeStepProps> = ({
  email,
  code,
  setCode,
  onVerify,
  onResend,
  onBack,
  isLoading,
  isResending,
  error,
  isRefusedCode = false,
  codeSentAt = null,
  securityCheck = null,
  securityCheckPending = false,
}) => {
  const intl = useIntl();
  const errorKind = authErrorKind(error);
  // The wait starts with the send of the code; a reload of the page keeps the rest of it.
  const resendWait = useCountdown(resendWaitLeft(codeSentAt));
  const startResendWait = resendWait.start;
  const [resent, setResent] = useState(false);
  const wasResending = useRef(false);

  useEffect(() => {
    if (codeSentAt !== null) {
      startResendWait(resendWaitLeft(codeSentAt));
    }
  }, [codeSentAt, startResendWait]);

  useEffect(() => {
    if (wasResending.current && !isResending && error === null) {
      setResent(true);
      startResendWait(RESEND_WAIT_SECONDS);
    }
    wasResending.current = isResending;
  }, [isResending, error, startResendWait]);

  useEffect(() => {
    if (errorKind === 'rateLimit') {
      setResent(false);
    }
  }, [errorKind, error]);

  const canResend = !isResending && resendWait.secondsLeft === 0 && !securityCheckPending;
  const codeLabel = intl.formatMessage(authV2Messages.codeLabel);

  return (
    <div className="au-step">
      <div className="au-head">
        <span className="au-eyebrow">
          <span className="dot" />
          {intl.formatMessage(authV2Messages.codeEyebrow)}
        </span>
        <h1 className="au-h sm">{intl.formatMessage(authV2Messages.codeTitle)}</h1>
        <p className="au-lead au-lead-email">
          {intl.formatMessage(authV2Messages.codeSentTo, { email, b: renderBold })}
        </p>
      </div>

      <form className="au-form" onSubmit={onVerify}>
        <AuthCodeInput
          value={code}
          onChange={setCode}
          onComplete={(digits) => onVerify(null, digits)}
          disabled={isLoading}
          error={errorKind === 'badCode'}
          ariaLabel={codeLabel}
        />

        {errorKind === 'badCode' ? (
          <AuthFieldError>{intl.formatMessage(authV2Messages.codeWrong)}</AuthFieldError>
        ) : null}
        {errorKind === 'rateLimit' ? (
          <AuthFieldError icon={faClock}>
            <AuthRateLimitText error={error} />
          </AuthFieldError>
        ) : null}
        {errorKind === 'turnstile' ? (
          <AuthFieldError>{intl.formatMessage(authV2Messages.securityCheckFailed)}</AuthFieldError>
        ) : null}
        {error !== null && (errorKind === 'other' || errorKind === 'disposable') ? <ErrorAlert error={error} /> : null}
        {resent && error === null ? (
          <AuthFieldError tone="ok" icon={faCheck}>
            {intl.formatMessage(authV2Messages.codeResent)}
            {resendWait.secondsLeft > 0 ? (
              <span className="au-faint">
                {' '}
                {intl.formatMessage(authV2Messages.codeResendIn, { time: formatCountdown(resendWait.secondsLeft) })}
              </span>
            ) : null}
          </AuthFieldError>
        ) : null}

        {securityCheck}
        <SMLButton
          color="secondary"
          onClick={onVerify}
          disabled={isLoading || code.length !== 6 || isRefusedCode}
          isLoading={isLoading}
          stretch
          omitCap
          size="lg"
          submit
        >
          {intl.formatMessage(authV2Messages.continue)}
          <FontAwesomeIcon icon={faChevronRight} />
        </SMLButton>

        <div className="au-code-meta">
          <span className="au-small">
            {intl.formatMessage(authV2Messages.codeNoEmail)}{' '}
            <button type="button" className="au-link" onClick={onResend} disabled={!canResend}>
              {resendWait.secondsLeft > 0 && !resent
                ? intl.formatMessage(authV2Messages.codeResendIn, { time: formatCountdown(resendWait.secondsLeft) })
                : intl.formatMessage(authV2Messages.codeResend)}
            </button>
          </span>
          <button type="button" className="au-link muted" onClick={onBack}>
            {intl.formatMessage(authV2Messages.codeChangeEmail)}
          </button>
        </div>
        <p className="au-small">{intl.formatMessage(authV2Messages.codeSpam, { sender: config.loginMailSender })}</p>
      </form>
    </div>
  );
};

export const AuthCodeStep = TypedMemo(AuthCodeStepComponent);
